<template>
  <b-sidebar
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="reset"
    @change="$emit('update:is-sidebar-active', $event)"
  >
    <div
      class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
    >
      <h5 class="mb-0">
        {{
          `${isAdd ? $t("actions.add") : $t("actions.edit")} ${$tc(
            "hanwha_alarm_rule.title",
            1
          )}`
        }}
      </h5>
      <feather-icon
        icon="XIcon"
        class="cursor-pointer"
        @click="$emit('update:is-sidebar-active', false)"
      />
    </div>
    <validation-observer ref="observer" #default="{ handleSubmit }">
      <b-form class="p-2" @submit.prevent="handleSubmit(submit)">
        <validation-provider #default="ctx" name="profile" rules="required">
          <b-form-group :label="$tc('hanwha_alarm_profile.title', 1)">
            <v-select
              v-model="form.id_hanwha_alarm_profile"
              :options="profileOptions"
              :reduce="(item) => item.value"
              :clearable="false"
              :disabled="profileId != null"
            />
            <b-form-invalid-feedback :state="state(ctx)">{{
              ctx.errors[0]
            }}</b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
        <validation-provider #default="ctx" name="type_log" rules="required">
          <b-form-group :label="$t('hanwha_alarm_rule.type_log')"
            ><v-select
              v-model="form.type_log"
              :options="logTypes"
              :reduce="(item) => item.value"
              :clearable="false"
              @input="onLogTypeChange"
            />
            <b-form-invalid-feedback :state="state(ctx)">{{
              ctx.errors[0]
            }}</b-form-invalid-feedback></b-form-group
          >
        </validation-provider>
        <validation-provider #default="ctx" name="event_type" rules="required">
          <b-form-group :label="$t('hanwha_alarm_rule.event_type')" :state="state(ctx)"
            ><v-select
              v-model="form.event_type"
              :options="eventTypeOptions"
              :clearable="false"
            />
            <b-form-invalid-feedback :state="state(ctx)">{{
              ctx.errors[0]
            }}</b-form-invalid-feedback></b-form-group
          >
        </validation-provider>
        <b-form-group :label="$t('hanwha_alarm_rule.recovery_event_type')"
          ><v-select v-model="form.recovery_event_type" :options="eventTypeOptions"
        /></b-form-group>
        <b-form-group :label="$t('hanwha_alarm_rule.channel')"
          ><b-form-input
            v-model.number="form.channel"
            type="number"
            min="1"
            :placeholder="$t('hanwha_alarm_rule.any_channel')"
        /></b-form-group>
        <validation-provider #default="ctx" name="alarm_type" rules="required">
          <b-form-group :label="$t('hanwha_alarm_rule.alarm_type')"
            ><v-select
              v-model="form.id_alarm_type"
              :options="alarmTypeOptions"
              :reduce="(item) => item.value"
              :clearable="false"
            />
            <b-form-invalid-feedback :state="state(ctx)">{{
              ctx.errors[0]
            }}</b-form-invalid-feedback></b-form-group
          >
        </validation-provider>
        <validation-provider #default="ctx" name="target" rules="required">
          <b-form-group :label="$t('hanwha_alarm_rule.target')"
            ><v-select
              v-model="form.target"
              :options="targetOptions"
              :reduce="(item) => item.value"
              :clearable="false"
            />
            <b-form-invalid-feedback :state="state(ctx)">{{
              ctx.errors[0]
            }}</b-form-invalid-feedback></b-form-group
          >
        </validation-provider>
        <b-form-group :label="$t('hanwha_alarm_rule.alarm_message')"
          ><b-form-input v-model.trim="form.alarm_message"
        /></b-form-group>
        <b-form-group :label="$t('hanwha_alarm_rule.recovery_message')"
          ><b-form-input v-model.trim="form.recovery_message"
        /></b-form-group>
        <b-row
          ><b-col
            ><b-form-group :label="$t('hanwha_alarm_rule.delay_seconds')"
              ><b-form-input
                v-model.number="form.delay_seconds"
                type="number"
                min="0" /></b-form-group
          ></b-col>
          <b-col
            ><b-form-group :label="$t('hanwha_alarm_rule.priority')"
              ><b-form-input
                v-model.number="form.priority"
                type="number"
                min="0" /></b-form-group></b-col
        ></b-row>
        <b-form-checkbox v-model="form.enabled" switch>{{
          $t("hanwha_alarm_rule.enabled")
        }}</b-form-checkbox>
        <div class="d-flex mt-2">
          <b-button type="submit" variant="primary" class="mr-1" :disabled="saving">{{
            $t("actions.save")
          }}</b-button>
          <b-button
            variant="outline-secondary"
            @click="$emit('update:is-sidebar-active', false)"
            >{{ $t("actions.cancel") }}</b-button
          >
        </div>
      </b-form>
    </validation-observer>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BRow,
  BCol,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import {
  HANWHA_LOG_TYPE_OPTIONS,
  getHanwhaEventTypes,
} from "@/views/hanwha/log/catalogs";

const blank = () => ({
  id: null,
  id_hanwha_alarm_profile: null,
  type_log: "systemlog",
  event_type: "",
  recovery_event_type: null,
  channel: null,
  id_alarm_type: null,
  target: "dvr",
  alarm_message: null,
  recovery_message: null,
  delay_seconds: 0,
  priority: 100,
  enabled: true,
});

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  props: {
    isSidebarActive: Boolean,
    isAdd: Boolean,
    itemEdit: Object,
    profileOptions: Array,
    profileId: {
      type: Number,
      default: null,
    },
    alarmTypeOptions: Array,
  },
  data() {
    return {
      required,
      form: blank(),
      saving: false,
      logTypes: HANWHA_LOG_TYPE_OPTIONS.filter((item) => item.value !== "accesslog"),
      targetOptions: [
        { value: "dvr", label: this.$t("hanwha_alarm_rule.dvr") },
        { value: "camera", label: this.$t("hanwha_alarm_rule.camera") },
      ],
    };
  },
  computed: {
    eventTypeOptions() {
      const catalog = getHanwhaEventTypes(this.form.type_log);
      const currentValues = [this.form.event_type, this.form.recovery_event_type].filter(
        Boolean
      );
      return [...new Set([...catalog, ...currentValues])];
    },
  },
  watch: {
    isSidebarActive(active) {
      if (active) {
        this.form = {
          ...blank(),
          ...(this.isAdd ? {} : this.itemEdit),
          id_hanwha_alarm_profile: this.profileId,
        };
      }
    },
  },
  methods: {
    state(ctx) {
      return ctx.dirty || ctx.validated ? !ctx.errors.length : null;
    },
    onLogTypeChange() {
      this.form.event_type = "";
      this.form.recovery_event_type = null;
    },
    reset() {
      this.form = blank();
      this.$refs.observer?.reset();
    },
    async submit() {
      this.saving = true;
      const payload = {
        ...this.form,
        channel: this.form.channel || null,
        recovery_event_type: this.form.recovery_event_type || null,
        recovery_message: this.form.recovery_message || null,
      };
      try {
        const response = this.isAdd
          ? await store.dispatch("hanwha_alarm_rules/add", payload)
          : await store.dispatch("hanwha_alarm_rules/edit", {
              id: this.form.id,
              params: payload,
            });
        this.$toast({
          component: ToastificationContent,
          props: { title: response.message, icon: "CheckIcon", variant: "success" },
        });
        this.$emit("saved");
        this.$emit("update:is-sidebar-active", false);
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.response?.data?.message || error.message,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
